import apiBase from './api.base';
import config from '../config/config';

// Types
export interface Post {
  id: number;
  title: { rendered: string };
  content?: { rendered: string };
  excerpt?: { rendered: string };
  date: string;
  author: number;
  featured_media: number;
  _embedded?: any;
}

export interface User {
  id: number;
  name: string;
  avatar_urls?: {
    '24'?: string;
    '48'?: string;
    '96'?: string;
  };
}

export interface ContactFormResponse {
  status: string;
  message: string;
  [key: string]: any;
}

// Get the base URL for media/content (for images, etc.)
export const getMediaBaseUrl = (): string => {
  const baseUrl = config.API_BASE_URL;
  // Remove /wp-admin/ to get the base URL for media
  if (baseUrl.endsWith('/wp-admin/')) {
    return baseUrl.replace('/wp-admin/', '');
  }
  return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
};

// Posts API
export const postsService = {
  // Get all posts
  getPosts: async (params?: {
    per_page?: number;
    page?: number;
    orderby?: string;
    order?: 'asc' | 'desc';
    search?: string;
    _embed?: boolean;
  }): Promise<Post[]> => {
    const queryParams = new URLSearchParams();
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.orderby) queryParams.append('orderby', params.orderby);
    if (params?.order) queryParams.append('order', params.order);
    if (params?.search) queryParams.append('search', params.search);
    if (params?._embed) queryParams.append('_embed', 'true');

    const response = await apiBase.get<Post[]>(`/posts?${queryParams.toString()}`);
    return response.data;
  },

  // Get a single post by ID
  getPost: async (id: number | string): Promise<Post> => {
    const response = await apiBase.get<Post>(`/posts/${id}?_embed`);
    return response.data;
  },

  // Search posts
  searchPosts: async (query: string, perPage: number = 5): Promise<Post[]> => {
    const response = await apiBase.get<Post[]>(
      `/posts?search=${encodeURIComponent(query)}&per_page=${perPage}&_embed`
    );
    return response.data;
  },

  // Get popular posts
  getPopularPosts: async (limit: number = 3): Promise<Post[]> => {
    const response = await apiBase.get<Post[]>(`/posts?per_page=${limit}&_embed`);
    return response.data;
  },

  // Get related posts
  getRelatedPosts: async (limit: number = 2, offset: number = 3): Promise<Post[]> => {
    const response = await apiBase.get<Post[]>(
      `/posts?per_page=${limit}&offset=${offset}&_embed`
    );
    return response.data;
  },
};

// Users API
export const usersService = {
  // Get user by ID
  getUser: async (id: number): Promise<User> => {
    const response = await apiBase.get<User>(`/users/${id}`);
    return response.data;
  },

  // Get multiple users
  getUsers: async (ids: number[]): Promise<User[]> => {
    const promises = ids.map((id) =>
      apiBase.get<User>(`/users/${id}`).catch(() => ({
        data: {
          id,
          name: 'Unknown',
          avatar_urls: {},
        } as User,
      }))
    );
    const results = await Promise.all(promises);
    return results.map((r) => r.data);
  },
};

// Contact Form API
export const contactService = {
  // Submit contact form
  submitContactForm: async (formData: FormData): Promise<ContactFormResponse> => {
    // Contact form uses a different endpoint (Contact Form 7 plugin)
    const baseUrl = config.API_BASE_URL;
    // Replace /wp-admin/ with /wp-json/contact-form-7/v1/contact-forms/9079810/feedback
    let contactUrl = baseUrl;
    if (baseUrl.endsWith('/wp-admin/')) {
      contactUrl = baseUrl.replace('/wp-admin/', '/wp-json/contact-form-7/v1/contact-forms/9079810/feedback');
    } else {
      const cleanUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      contactUrl = `${cleanUrl}/wp-json/contact-form-7/v1/contact-forms/9079810/feedback`;
    }

    const response = await fetch(contactUrl, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  },
};

// Banner Ads API
export const bannerService = {
  getBannerAds: async (): Promise<Post[]> => {
    const response = await apiBase.get<Post[]>(`/banner-ads?_embed`);
    return response.data;
  },
};

// Export all services
export default {
  postsService,
  usersService,
  contactService,
  bannerService,
  getMediaBaseUrl,
};

